import { useStaticQuery, graphql } from 'gatsby';
import invariant from 'tiny-invariant';
import handlebars from 'handlebars';
import capitalize from 'lodash/capitalize';
import indefinite from 'indefinite';

export interface SnippetProps {
	name: string;
	variables?: {
		[key: string]: string | string[] | { [key: string]: string | string[] };
	};
}

/**
 * A re-usable markdown snippet.
 *
 * It supports variables in the markdown body using handlebars.js
 * For example, given the variables: { animal: 'cat' }
 * {{ animal }} will be replaced with 'cat'
 * @see https://handlebarsjs.com for more information.
 */
export default function useSnippet(
	name: SnippetProps['name'],
	variables?: SnippetProps['variables'],
) {
	const data = useStaticQuery(graphql`
		query {
			allMdx(filter: { fileAbsolutePath: { regex: "/design-system-docs/src/snippets/" } }) {
				nodes {
					slug
					body
				}
			}
		}
	`);

	const mdx = data.allMdx.nodes.find((mdx: any) => mdx.slug === name);

	invariant(
		mdx,
		`Snippet '${name}' not found at 'services/design-system-docs/src/snippets/${name}.mdx'`,
	);

	// Captializes the first letter of a string.
	handlebars.registerHelper('capitalize', function (string) {
		return capitalize(string);
	});

	// Indefinite articles. Adds 'a' or 'an' before a string.
	handlebars.registerHelper('a', function (string) {
		return indefinite(string);
	});

	// Capitalizes indefinite articles. Adds 'A' or 'An' before a string.
	handlebars.registerHelper('capitalizeA', function (string) {
		return capitalize(indefinite(string));
	});

	// Logs a warning if a variable is missing.
	handlebars.registerHelper('helperMissing', function () {
		var options = arguments[arguments.length - 1];

		// We can't throw an error here, because cases where JS is used within MDX
		// may cause false positives when objects are detected as templating tags,
		// such as <MyComponent data={{ isSelected }} />
		console.warn(
			`Snippet file 'services/design-system-docs/src/snippets/${name}.mdx' may be missing a variable: '${options.name}'`,
		);
	});

	// Replace variables inside braces the markdown body.
	let bodyWithVariables = handlebars.compile(mdx.body)(variables);

	return bodyWithVariables;
}
